/**
 * useOrgAvailableTypes.ts
 *
 * Hook for fetching the org-wide set of schema types the calling user can
 * view (platform/system types ∪ the caller's SpiceDB-viewable types),
 * for applicability authoring. Org and identity are resolved server-side
 * from the token — this hook takes no sphere/org id parameter.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useImpersonation } from '@center-technologies/shared-api-clients';
import type { ApiClients } from '../context/ApiClientsContext';
import type { OrgAvailableTypeSchema, SchemaSource } from 'types/schema';

export type { SchemaSource };

/** Data returned by GET /schemas/org-available-types */
export interface OrgAvailableTypesData {
  schemas: OrgAvailableTypeSchema[];
  approachSources: Record<string, string[]>;
  sources: Record<string, SchemaSource>;
}

/** Return value of the useOrgAvailableTypes hook */
export interface UseOrgAvailableTypesReturn extends OrgAvailableTypesData {
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

const EMPTY_SCHEMAS: OrgAvailableTypeSchema[] = [];
const EMPTY_APPROACH_SOURCES: Record<string, string[]> = {};
const EMPTY_SOURCES: Record<string, SchemaSource> = {};

/**
 * Fetches the org-wide available schema types for the calling user
 * (`GET /schemas/org-available-types` — no id param; org + identity come
 * from the token). Fail-closed: any error clears the list rather than
 * leaving a stale one on screen, since a stale list could offer a type the
 * caller no longer has `view` on.
 *
 * @param apiClients - API clients (pass null if not ready)
 */
export function useOrgAvailableTypes(apiClients: ApiClients | null): UseOrgAvailableTypesReturn {
  // The route resolves org + identity from the token, so switching
  // impersonation changes the answer without changing any argument. Track it
  // so the fetch re-runs — otherwise the previous principal's types stay on
  // screen (and the server cache is keyed per identity for 5 minutes).
  const { impersonationDetails } = useImpersonation();
  const impersonatedOrganizationId = impersonationDetails?.organizationId;

  const [schemas, setSchemas] = useState<OrgAvailableTypeSchema[]>(EMPTY_SCHEMAS);
  const [approachSources, setApproachSources] = useState<Record<string, string[]>>(EMPTY_APPROACH_SOURCES);
  const [sources, setSources] = useState<Record<string, SchemaSource>>(EMPTY_SOURCES);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchOrgAvailableTypes = useCallback(
    async (skipCache = false) => {
      if (!apiClients?.schemaClient) return;

      setLoading(true);
      setError(null);

      try {
        const params: Record<string, string> = {};
        if (skipCache) {
          params.skipCache = 'true';
        }
        const data = await apiClients.schemaClient.get<OrgAvailableTypesData>(
          '/schemas/org-available-types',
          { params }
        );

        if (isMountedRef.current) {
          setSchemas(data.schemas);
          setApproachSources(data.approachSources);
          setSources(data.sources);
        }
      } catch (err: unknown) {
        if (isMountedRef.current) {
          // Fail-closed: never leave a stale, possibly-now-unviewable list on screen.
          setSchemas(EMPTY_SCHEMAS);
          setApproachSources(EMPTY_APPROACH_SOURCES);
          setSources(EMPTY_SOURCES);
          setError(
            err instanceof Error ? err.message : 'Failed to fetch org available types'
          );
        }
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    },
    [apiClients]
  );

  // Fetch on mount, when apiClients changes, and when the impersonated
  // principal changes (the token — not any argument — selects the result set).
  useEffect(() => {
    if (!apiClients?.schemaClient) {
      setSchemas(EMPTY_SCHEMAS);
      setApproachSources(EMPTY_APPROACH_SOURCES);
      setSources(EMPTY_SOURCES);
      return;
    }

    fetchOrgAvailableTypes();
  }, [apiClients, fetchOrgAvailableTypes, impersonatedOrganizationId]);

  const refetch = useCallback(() => {
    fetchOrgAvailableTypes(true);
  }, [fetchOrgAvailableTypes]);

  return { schemas, approachSources, sources, loading, error, refetch };
}
